import { useMemo } from 'react';
import {
    buildRequestIndex,
    useMessagesStore,
} from '../store/messagesStore';
import { formatBytes, formatLatency, percentile } from '../lib/format';
import { buildValidationMap, summarizeValidation } from '../lib/validation';
import { SpecHint } from './SpecHint';

export function StatsBar() {
    const messages = useMessagesStore((s) => s.messages);

    const stats = useMemo(() => {
        let req = 0;
        let res = 0;
        let note = 0;
        let err = 0;
        let bytes = 0;
        const tsBySeq = new Map<number, number>();
        for (const m of messages) {
            tsBySeq.set(m.seq, m.timestamp);
            bytes += m.raw.length;
            switch (m.kind) {
                case 'request':
                    req += 1;
                    break;
                case 'response':
                    res += 1;
                    break;
                case 'notification':
                    note += 1;
                    break;
                case 'error':
                    err += 1;
                    break;
                default:
                    break;
            }
        }
        const pairs = buildRequestIndex(messages);
        const latencies: number[] = [];
        for (const m of messages) {
            if (m.kind !== 'response' && m.kind !== 'error') continue;
            const reqSeq = pairs.get(m.seq);
            if (reqSeq === undefined) continue;
            const reqTs = tsBySeq.get(reqSeq);
            if (reqTs === undefined) continue;
            latencies.push(m.timestamp - reqTs);
        }
        latencies.sort((a, b) => a - b);
        const p50 = latencies.length ? percentile(latencies, 50) : null;
        const p99 = latencies.length ? percentile(latencies, 99) : null;
        const spec = summarizeValidation(buildValidationMap(messages));
        return { total: messages.length, req, res, note, err, bytes, p50, p99, spec };
    }, [messages]);

    return (
        <footer className="flex items-center justify-between gap-6 border-t border-line bg-surface-elev/80 px-5 py-1.5 font-mono text-[10px] uppercase tracking-widest text-ink-muted">
            <div className="flex items-center gap-5">
                <Stat label="msgs" value={stats.total} tone="primary" />
                <Stat label="req" value={stats.req} tone="out" />
                <Stat label="rsp" value={stats.res} tone="in" />
                <Stat label="ntf" value={stats.note} tone="note" />
                <Stat label="err" value={stats.err} tone={stats.err > 0 ? 'error' : 'muted'} />
            </div>
            <div className="flex items-center gap-5">
                <Stat
                    label="p50"
                    value={stats.p50 !== null ? formatLatency(stats.p50) : '—'}
                    tone="primary"
                />
                <Stat
                    label="p99"
                    value={stats.p99 !== null ? formatLatency(stats.p99) : '—'}
                    tone="primary"
                />
                <Stat
                    label="bytes"
                    value={stats.total > 0 ? formatBytes(stats.bytes) : '—'}
                    tone="primary"
                />
                {stats.spec.checked > 0 && (
                    <SpecStat
                        invalidFrames={stats.spec.invalidFrames}
                        totalErrors={stats.spec.totalErrors}
                        affectedMethods={stats.spec.affectedMethods}
                        advisoryFrames={stats.spec.advisoryFrames}
                        totalAdvisories={stats.spec.totalAdvisories}
                    />
                )}
                <StarPrompt />
            </div>
        </footer>
    );
}

function StarPrompt() {
    return (
        <a
            href="https://github.com/maksugr/acp-devtools"
            target="_blank"
            rel="noreferrer noopener"
            title="Enjoying acp-devtools? Star it on GitHub — thank you!"
            aria-label="Star acp-devtools on GitHub"
            className="inline-flex items-baseline gap-1 text-ink-muted transition-colors hover:text-accent-warn focus-visible:text-accent-warn focus-visible:outline-none"
        >
            <span aria-hidden="true" className="text-[10px]">★</span>
            <span className="text-[9px] tracking-normal">Star on GitHub</span>
        </a>
    );
}

function SpecStat({
    invalidFrames,
    totalErrors,
    affectedMethods,
    advisoryFrames,
    totalAdvisories,
}: {
    invalidFrames: number;
    totalErrors: number;
    affectedMethods: string[];
    advisoryFrames: number;
    totalAdvisories: number;
}) {
    const advisoryMarker =
        advisoryFrames > 0 ? (
            <SpecHint
                tone="warn"
                label={
                    <span className="font-sans">
                        {totalAdvisories} field{totalAdvisories === 1 ? '' : 's'} a future ACP
                        version will require {totalAdvisories === 1 ? 'is' : 'are'} missing — valid
                        today
                    </span>
                }
            >
                <span className="text-accent-warn">⚠ {advisoryFrames}</span>
            </SpecHint>
        ) : null;

    if (invalidFrames === 0) {
        return (
            <span className="inline-flex items-baseline gap-1.5">
                <SpecHint
                    label={
                        <span className="font-sans">
                            every frame validates against the ACP schema
                        </span>
                    }
                    tone="info"
                >
                    <span className="inline-flex items-baseline gap-1">
                        <span>spec</span>
                        <span className="text-accent-ok">✓</span>
                    </span>
                </SpecHint>
                {advisoryMarker}
            </span>
        );
    }
    const summary = `${totalErrors} schema error${totalErrors === 1 ? '' : 's'} in ${invalidFrames} frame${invalidFrames === 1 ? '' : 's'}`;
    return (
        <span className="inline-flex items-baseline gap-1.5">
            <SpecHint
                tone="warn"
                label={
                    <div className="space-y-1.5 font-sans">
                        <div>{summary}</div>
                        {affectedMethods.length > 0 && (
                            <div className="font-mono text-[10px] text-ink-muted">
                                methods: {affectedMethods.join(', ')}
                            </div>
                        )}
                    </div>
                }
            >
                <span className="inline-flex items-baseline gap-1">
                    <span>spec</span>
                    <span className="text-accent-error">⚠ {invalidFrames}</span>
                </span>
            </SpecHint>
            {advisoryMarker}
        </span>
    );
}

function Stat({
    label,
    value,
    tone,
}: {
    label: string;
    value: string | number;
    tone: 'primary' | 'out' | 'in' | 'note' | 'error' | 'muted';
}) {
    const cls =
        tone === 'primary'
            ? 'text-ink-primary'
            : tone === 'out'
              ? 'text-accent-out'
              : tone === 'in'
                ? 'text-accent-in'
                : tone === 'note'
                  ? 'text-accent-note'
                  : tone === 'error'
                    ? 'text-accent-error'
                    : 'text-ink-muted';
    return (
        <span className="inline-flex items-baseline gap-1">
            <span>{label}</span>
            <span className={cls}>{value}</span>
        </span>
    );
}
